import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Box, FormControl, Link as MuiLink, TextField, Typography } from "@mui/material";
import { AuthError } from "aws-amplify/auth";
import type React from "react";
import { useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { cognitoErrorMessages, cognitoErrorNames, useAuth } from "../hooks/useAuth";
import { type LoginFormValue, loginSchema } from "../schemas";

export const Login = (): React.ReactElement => {
	const { handleSignIn, currentAuthenticatedUser, isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const emailId = useId();
	const passwordId = useId();

	const email = searchParams.get("email") || "";

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors,
	} = useForm<LoginFormValue>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email,
		},
	});

	const onSubmit = async (data: LoginFormValue) => {
		clearErrors("root");
		setIsLoading(true);
		try {
			const signInResponse = await handleSignIn({
				username: data.email,
				password: data.password,
			});
			if (!signInResponse.isSignedIn) {
				if (signInResponse.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
					const params = new URLSearchParams();
					params.set("email", data.email);
					navigate(`/init-pw?${params}`);
					return;
				}
				if (signInResponse.nextStep.signInStep === "RESET_PASSWORD") {
					const params = new URLSearchParams();
					params.set("email", data.email);
					setError("root", {
						message: "パスワードのリセットが必要です。",
					});
					return;
				}
			}
			// 認証状態を確実に更新してからナビゲート
			await currentAuthenticatedUser();
			navigate("/");
		} catch (err) {
			if (err instanceof AuthError) {
				switch (err.name) {
					case cognitoErrorNames.SignIn.UserNotFound: {
						setError("email", {
							message: "メールアドレスが正しくありません。",
						});
						break;
					}
					case cognitoErrorNames.SignIn.NotAuthorized: {
						switch (err.message) {
							case cognitoErrorMessages.SignIn.Incorrect:
								setError("email", {
									message: "メールアドレスまたはパスワードが正しくありません。",
								});
								setError("password", {
									message: "メールアドレスまたはパスワードが正しくありません。",
								});
								break;
							case cognitoErrorMessages.SignIn.UserDisabled:
								setError("email", {
									message: "このユーザーは無効化されています。",
								});
								break;
							case cognitoErrorMessages.SignIn.TemporaryPasswordExpired:
								setError("password", {
									message:
										"仮パスワードの有効期限が切れています。管理者にリセットを依頼してください。",
								});
								break;
						}
						break;
					}
					case cognitoErrorNames.SignIn.UserAlreadyAuthenticated: {
						alert("既にログインしています。");
						await currentAuthenticatedUser();
						navigate("/");
						break;
					}
					default: {
						setError("root", { message: "予期しないエラーが発生しました。" });
						break;
					}
				}
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (isAuthenticated) {
			navigate("/");
		}
	}, [isAuthenticated, navigate]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				border: "1px solid #d0d7de",
				borderRadius: 1,
				padding: 2,
				backgroundColor: "#f5f5f5",
			}}
		>
			<Typography component="h2" variant="h5">
				ログイン
			</Typography>
			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				sx={{
					width: "100%",
					mt: 1,
				}}
			>
				<FormControl fullWidth>
					<TextField
						margin="normal"
						required
						id={emailId}
						label="メールアドレス"
						autoComplete="email"
						placeholder="email@example.com"
						autoFocus
						sx={{
							backgroundColor: "white",
							"& .MuiFormHelperText-root": {
								backgroundColor: "transparent",
							},
						}}
						{...register("email")}
						error={Boolean(errors.email)}
					/>
					<Typography variant="caption" color="error">
						{errors.email?.message}
					</Typography>
				</FormControl>
				<FormControl fullWidth>
					<TextField
						margin="normal"
						required
						label="パスワード"
						type="password"
						placeholder="パスワードを入力してください"
						id={passwordId}
						autoComplete="current-password"
						sx={{
							backgroundColor: "white",
						}}
						{...register("password")}
						error={Boolean(errors.password)}
					/>
					<Typography variant="caption" color="error">
						{errors.password?.message}
					</Typography>
				</FormControl>
				<MuiLink
					component={Link}
					to="/forgot-pw"
					tabIndex={-1}
					sx={{
						ml: 0.5,
						display: "inline-block",
					}}
				>
					パスワードを忘れた場合
				</MuiLink>
				<LoadingButton
					type="submit"
					fullWidth
					variant="contained"
					loading={isLoading}
					sx={{ mt: 3, mb: 2, fontWeight: "bold" }}
				>
					ログイン
				</LoadingButton>
			</Box>
		</Box>
	);
};
